import React, { createContext, useContext, useState, useEffect } from 'react';
import { getApiUrl } from '@/utils/authUtils';
import { apiFetch } from '@/hooks/useFetch';

const API_URL = getApiUrl();

const GuestContext = createContext(null);

// Generate or retrieve guest ID
const getGuestId = () => {
  let guestId = localStorage.getItem('clearcomm-guest-id');
  if (!guestId) {
    guestId = 'guest_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
    localStorage.setItem('clearcomm-guest-id', guestId);
  }
  return guestId;
};

export const GuestProvider = ({ children }) => {
  const [guestId] = useState(getGuestId);
  const [guestData, setGuestData] = useState(() => {
    const saved = localStorage.getItem('clearcomm-guest-data');
    if (saved) {
      const data = JSON.parse(saved);
      // Reset if it's a new day
      const today = new Date().toISOString().split('T')[0];
      if (data.date !== today) {
        return { date: today, phrasesViewed: [], visitTracked: false };
      }
      return data;
    }
    return { date: new Date().toISOString().split('T')[0], phrasesViewed: [], visitTracked: false };
  });
  
  const GUEST_DAILY_PHRASE_LIMIT = 5;
  const GUEST_CHECKLIST_LIMIT = 5;
  
  // Track guest visit on first load of the day
  useEffect(() => {
    const trackVisit = async () => {
      if (guestData.visitTracked) return;
      
      try {
        await apiFetch('/api/guest/track-visit', {
          method: 'POST',
          body: { guest_id: guestId }
        });
        
        setGuestData(prev => {
          const newData = { ...prev, visitTracked: true };
          localStorage.setItem('clearcomm-guest-data', JSON.stringify(newData));
          return newData;
        });
      } catch (error) {
        console.error('Failed to track guest visit:', error);
      }
    };
    
    trackVisit();
  }, [guestId, guestData.visitTracked]);
  
  // Save guest data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('clearcomm-guest-data', JSON.stringify(guestData));
  }, [guestData]);
  
  // Check if guest can view a phrase
  const canGuestViewPhrase = (phraseId) => {
    if (guestData.phrasesViewed.includes(phraseId)) {
      return true; // Already viewed today
    }
    return guestData.phrasesViewed.length < GUEST_DAILY_PHRASE_LIMIT;
  };
  
  // Record that guest viewed a phrase
  const recordGuestPhraseView = (phraseId) => {
    if (guestData.phrasesViewed.includes(phraseId)) return;
    
    setGuestData(prev => {
      const newData = {
        ...prev,
        phrasesViewed: [...prev.phrasesViewed, phraseId]
      };
      localStorage.setItem('clearcomm-guest-data', JSON.stringify(newData));
      return newData;
    });
  };
  
  // Get remaining free views for guest
  const getGuestRemainingViews = () => {
    return Math.max(0, GUEST_DAILY_PHRASE_LIMIT - guestData.phrasesViewed.length);
  };
  
  return (
    <GuestContext.Provider value={{
      guestId,
      isGuest: true,
      guestData,
      canGuestViewPhrase,
      recordGuestPhraseView,
      getGuestRemainingViews,
      GUEST_DAILY_PHRASE_LIMIT,
      GUEST_CHECKLIST_LIMIT
    }}>
      {children}
    </GuestContext.Provider>
  );
};

export const useGuest = () => {
  const context = useContext(GuestContext);
  if (!context) {
    throw new Error('useGuest must be used within a GuestProvider');
  }
  return context;
};

export default GuestContext;
